<script setup lang="ts">
import { Head, Link, usePage } from '@inertiajs/vue3';
import {
    ArrowRight,
    BarChart3,
    CheckCircle,
    Database,
    FileText,
    FolderOpen,
    Plus,
    Zap,
} from 'lucide-vue-next';
import { computed } from 'vue';
import { show } from '@/actions/App/Http/Controllers/ProjectController';
import StatCard from '@/components/shared/StatCard.vue';
import { Badge } from '@/components/ui/badge';
import { dashboard } from '@/routes';
import { create as projectsCreate } from '@/routes/projects';

defineOptions({
    layout: {
        breadcrumbs: [
            {
                title: 'Accueil',
                href: dashboard(),
            },
        ],
    },
});

interface DashboardProject {
    id: string;
    name: string;
    description?: string | null;
    status?: string | null;
    papers_count: number;
    included_count: number;
    excluded_count: number;
    runs_count: number;
    created_at: string;
}

interface Props {
    projects: DashboardProject[];
    stats: {
        projects_count: number;
        papers_count: number;
        runs_count: number;
        screening_percent: number;
    };
}

const props = defineProps<Props>();

const page = usePage();
const user = computed(() => page.props.auth.user);
const firstName = computed(
    () => (user.value as { first_name?: string })?.first_name ?? 'chercheur',
);

function screeningPercent(project: DashboardProject): number {
    if (project.papers_count === 0) {
        return 0;
    }

    return Math.round(
        ((project.included_count + project.excluded_count) /
            project.papers_count) *
            100,
    );
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}
</script>

<template>
    <Head title="Accueil" />

    <div v-if="props.stats" class="flex flex-col gap-6 p-6">
        <!-- Header -->
        <div class="flex flex-wrap items-center justify-between gap-4">
            <div>
                <h1 class="text-2xl font-semibold tracking-tight">
                    Bienvenue, {{ firstName }}
                </h1>
                <p class="text-muted-foreground">
                    Voici un aperçu de vos projets et de votre activité.
                </p>
            </div>
        </div>

        <!-- Stat Cards -->
        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
                title="Projets actifs"
                :value="props.stats.projects_count"
                :icon="FolderOpen"
            />
            <StatCard
                title="Articles importés"
                :value="props.stats.papers_count"
                :icon="FileText"
            />
            <StatCard
                title="Analyses IA"
                :value="props.stats.runs_count"
                :icon="Zap"
            />
            <StatCard
                title="Avancement de la sélection"
                :value="`${props.stats.screening_percent}%`"
                :icon="CheckCircle"
            />
        </div>

        <!-- Project List -->
        <div>
            <h2 class="mb-4 text-lg font-semibold tracking-tight">
                Vos projets
            </h2>

            <div
                v-if="props.projects.length"
                class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
                <Link
                    v-for="project in props.projects"
                    :key="project.id"
                    :href="show.url(project.id)"
                    class="group flex flex-col justify-between rounded-2xl border border-border/40 bg-card p-6 transition-colors hover:border-border"
                >
                    <div>
                        <h3
                            class="font-semibold tracking-tight transition-colors group-hover:text-brand"
                        >
                            {{ project.name }}
                        </h3>
                        <p
                            v-if="project.description"
                            class="mt-1 line-clamp-1 text-sm text-muted-foreground"
                        >
                            {{ project.description }}
                        </p>
                    </div>

                    <div class="mt-4 space-y-3">
                        <!-- Badges -->
                        <div class="flex flex-wrap items-center gap-2">
                            <Badge variant="secondary" class="text-xs">
                                {{ project.papers_count }} article{{
                                    project.papers_count !== 1 ? 's' : ''
                                }}
                            </Badge>
                            <Badge variant="secondary" class="text-xs">
                                {{ project.runs_count }} analyse{{
                                    project.runs_count !== 1 ? 's' : ''
                                }}
                            </Badge>
                        </div>

                        <!-- Screening Progress -->
                        <div v-if="project.papers_count > 0">
                            <div
                                class="mb-1 flex items-center justify-between text-xs text-muted-foreground"
                            >
                                <span>Sélection des articles</span>
                                <span>{{ screeningPercent(project) }}%</span>
                            </div>
                            <div
                                class="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                            >
                                <div
                                    class="h-full rounded-full bg-brand transition-all"
                                    :style="{
                                        width: `${screeningPercent(project)}%`,
                                    }"
                                />
                            </div>
                        </div>

                        <!-- Footer -->
                        <div class="flex items-center justify-between pt-1">
                            <span class="text-xs text-muted-foreground">
                                {{ formatDate(project.created_at) }}
                            </span>
                            <ArrowRight
                                class="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                            />
                        </div>
                    </div>
                </Link>
            </div>

            <div
                v-else
                class="mt-2 rounded-2xl border border-dashed border-border p-8"
            >
                <div class="text-center">
                    <h3 class="text-lg font-semibold">
                        Démarrez votre première revue systématique
                    </h3>
                    <p
                        class="mx-auto mt-2 max-w-md text-sm text-muted-foreground"
                    >
                        Rewio automatise le workflow PRISMA complet : import,
                        screening IA, extraction de données structurée et
                        génération du diagramme PRISMA 2020.
                    </p>
                </div>

                <div class="mt-6 grid gap-3 sm:grid-cols-3">
                    <div
                        class="rounded-xl border border-border/50 bg-muted/30 p-4 text-left"
                    >
                        <FileText class="mb-2 h-5 w-5 text-brand" />
                        <p class="text-sm font-semibold">
                            Import & Screening IA
                        </p>
                        <p class="mt-1 text-xs text-muted-foreground">
                            Importez depuis Zotero ou des fichiers RIS/BibTeX.
                            L'IA propose des décisions de sélection.
                        </p>
                    </div>
                    <div
                        class="rounded-xl border border-border/50 bg-muted/30 p-4 text-left"
                    >
                        <Database class="mb-2 h-5 w-5 text-brand" />
                        <p class="text-sm font-semibold">
                            Extraction structurée
                        </p>
                        <p class="mt-1 text-xs text-muted-foreground">
                            Définissez une grille d'extraction, l'IA extrait les
                            données des PDF automatiquement.
                        </p>
                    </div>
                    <div
                        class="rounded-xl border border-border/50 bg-muted/30 p-4 text-left"
                    >
                        <BarChart3 class="mb-2 h-5 w-5 text-brand" />
                        <p class="text-sm font-semibold">
                            Diagramme PRISMA auto
                        </p>
                        <p class="mt-1 text-xs text-muted-foreground">
                            Générez et exportez votre diagramme PRISMA 2020 en
                            temps réel.
                        </p>
                    </div>
                </div>

                <div class="mt-6 text-center">
                    <Link
                        :href="projectsCreate().url"
                        class="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90"
                    >
                        <Plus class="h-4 w-4" />
                        Créer mon premier projet
                    </Link>
                </div>
            </div>
        </div>
    </div>
</template>
